import { Inject, Injectable } from '@angular/core';
import { CollectionMigration, MigrationsService } from './migrations.service';
import { Observable, zip } from 'rxjs';
import { SlbNotificationItem } from '@slb-dls/angular-material/notifications-panel';
import { map } from 'rxjs/operators';
import { LOCAL_STORAGE, StorageService } from 'ngx-webstorage-service';
import { DownloadTask, SimulationDownloadService } from './simulation-download.service';

@Injectable({
  providedIn: 'root'
})
export class UserNotificationService {
  private readonly readKey: string = 'read-notifications';
  private readonly removedKey: string = 'removed-notifications';
  private readonly dateKey: string = 'notifications-date';

  constructor(
    private migrationsService: MigrationsService,
    private simulationDownloadService: SimulationDownloadService,
    @Inject(LOCAL_STORAGE) private localStorage: StorageService<UserNotification[]>,
    @Inject(LOCAL_STORAGE) private dateStorage: StorageService<number>
  ) { }

  getAllNotificationsForToday(): Observable<UserNotification[]> {
    const dayOfMonth = new Date().getDate();
    let readNotifications = [];
    let removedNotifications = [];
    if (dayOfMonth !== this.dateStorage.get(this.dateKey)) {
      this.dateStorage.set(this.dateKey, dayOfMonth);
      this.localStorage.set(this.readKey, readNotifications);
      this.localStorage.set(this.removedKey, removedNotifications);
    } else {
      readNotifications = this.localStorage.get(this.readKey);
      removedNotifications = this.localStorage.get(this.removedKey);
    }

    return zip(this.migrationsService.getMigrationsForToday().pipe(
      map(migrations => {
        return migrations
          .filter(p => {
            return !removedNotifications.some(r => this.compareMigrationToNotification(p, r));
          })
          .map(p => {
            return {
              read: readNotifications.some(r => this.compareMigrationToNotification(p, r)),
              error: p.collectionMigrationStatusId == p.collectionMigrationStatus.failed,
              success: p.collectionMigrationStatusId == p.collectionMigrationStatus.completed,
              timedOut: p.collectionMigrationStatusId == p.collectionMigrationStatus.timedOut,
              data: p,
              type: 'Migration',
              text: `${p.collectionMigrationStatus.name} - ${p.targetCollectionName}`,
              date: new Date(`${p.lastAttemptedDateTimeUtc ?? p.createdDateTimeUtc}z`)
            } as UserNotification;
          });
      })
    ), this.simulationDownloadService.getDownloadsForToday().pipe(
      map(downloadTasks => {
        return downloadTasks
          .filter(p => {
            return !removedNotifications.some(r => this.compareDownloadTaskToNotification(p, r));
          })
          .map(p => {
            return {
              read: readNotifications.some(r => this.compareDownloadTaskToNotification(p, r)),
              error: p.downloadTaskStatusId == p.downloadTaskStatus.failed || p.downloadTaskStatusId == p.downloadTaskStatus.compressionFailed,
              success: p.downloadTaskStatusId == p.downloadTaskStatus.completed,
              timedOut: p.downloadTaskStatusId == p.downloadTaskStatus.timedOut,
              data: p,
              type: 'DownloadTask',
              text: p.downloadTaskStatusId == p.downloadTaskStatus.failed || p.downloadTaskStatusId == p.downloadTaskStatus.compressionFailed ?
                              `retrying a failed task - ${p.collectionName}`:`${p.downloadTaskStatus.name} - ${p.collectionName}`,
              date: new Date(`${p.lastAttemptedDateTimeUtc ?? p.createdDateTimeUtc}z`)
            } as UserNotification;
          });
      })
    )).pipe(map(([migrations, downloadTasks]) => {
      const merged = [...migrations, ...downloadTasks];
      return merged;
    }));
  }

  private compareDownloadTaskToNotification(downloadTask: DownloadTask, notification: UserNotification): boolean {
    return (
      notification.data.id === downloadTask.id &&
      notification.type === 'DownloadTask' &&
      downloadTask.downloadTaskStatusId === notification.data.downloadTaskStatusId
    );
  }


  private compareMigrationToNotification(migration: CollectionMigration, notification: UserNotification): boolean {
    return (
      notification.data.id === migration.id &&
      notification.type === 'Migration' &&
      migration.collectionMigrationStatusId === notification.data.collectionMigrationStatusId
    );
  }

  markAsRead(userNotification: UserNotification) {
    let existingValues = this.localStorage.get(this.readKey);
    if (!existingValues) existingValues = [];
    existingValues.push(userNotification);
    this.localStorage.set(this.readKey, existingValues);
  }

  removeNotification(userNotification: UserNotification) {
    let existingValues = this.localStorage.get(this.removedKey);
    if (!existingValues) existingValues = [];
    existingValues.push(userNotification);
    this.localStorage.set(this.removedKey, existingValues);
  }
}

export interface UserNotification extends SlbNotificationItem {
  data: any;
  type: 'Migration' | 'DownloadTask';
}
